import "@/App.css";
import { Code, Palette, LineChart, Phone, Mail, ArrowRight, Check } from "lucide-react";
import { Button } from "./components/ui/button";

// Hero Section
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="min-h-screen flex items-center bg-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230F172A' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }} />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-32 relative z-10">
        <div className="max-w-3xl">
          {/* Caption */}
          <p className="animate-on-load animate-fade-in-up text-sm font-medium tracking-widest uppercase text-slate-500 mb-6">
            Web Development & Design
          </p>
          
          {/* Main Heading */}
          <h1 className="animate-on-load animate-fade-in-up animate-delay-100 font-outfit text-5xl md:text-7xl font-bold tracking-tight leading-none text-slate-900 mb-6">
            Building Digital<br />
            <span className="text-blue-600">Experiences.</span>
          </h1>
          
          {/* Subheading */}
          <p className="animate-on-load animate-fade-in-up animate-delay-200 text-base md:text-lg leading-relaxed text-slate-600 mb-10 max-w-xl">
            Professional web development and design services that help your business stand out online. Clean, modern, and effective solutions.
          </p>
          
          {/* CTAs */}
          <div className="animate-on-load animate-fade-in-up animate-delay-300 flex flex-col sm:flex-row gap-4">
            <Button 
              data-testid="cta-services-btn"
              className="btn-primary bg-blue-600 hover:bg-blue-700 text-white font-outfit font-medium px-8 py-6 rounded-full text-base"
              onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Our Services
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              data-testid="cta-contact-btn"
              variant="outline"
              className="btn-secondary border-slate-300 text-slate-700 hover:bg-slate-100 font-outfit font-medium px-8 py-6 rounded-full text-base"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Contact Us
            </Button>
          </div>
        </div>
      </div>
      
      {/* Decorative element */}
      <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-1/3 h-[600px] bg-slate-50 rounded-l-[60px]" />
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Code,
      title: "Web Development",
      description: "Custom websites built with modern technologies. Fast, responsive, and optimized for search engines.",
      features: ["React & Next.js", "Responsive Design", "SEO Optimized"]
    },
    {
      icon: Palette,
      title: "UI/UX Design",
      description: "Clean, user-centric designs that convert. We focus on usability and beautiful aesthetics.",
      features: ["Modern Interfaces", "User Research", "Brand Identity"]
    },
    {
      icon: LineChart,
      title: "Digital Strategy",
      description: "Consulting to help your business grow online. From concept to deployment and beyond.",
      features: ["Growth Planning", "Performance", "Analytics"]
    }
  ];

  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <div className="mb-16">
          <p className="text-sm font-medium tracking-widest uppercase text-blue-600 mb-4">
            What We Do
          </p>
          <h2 className="font-outfit text-3xl md:text-5xl font-semibold tracking-tight text-slate-900">
            Services
          </h2>
        </div>
        
        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              data-testid={`service-card-${index}`}
              className="service-card bg-white p-8 rounded-2xl border border-slate-200"
            >
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                <service.icon className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">
                {service.title}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {service.description}
              </p>
              <ul className="space-y-2">
                {service.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-center text-sm text-slate-600">
                    <Check className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" data-testid="about-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100">
              <img 
                src="https://images.unsplash.com/photo-1633198362880-4864a5d6fa30?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Modern workspace"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Decorative accent */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-600 rounded-2xl -z-10" />
          </div>
          
          {/* Content */}
          <div>
            <p className="text-sm font-medium tracking-widest uppercase text-blue-600 mb-4">
              About Us
            </p>
            <h2 className="font-outfit text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
              Roquel Digital Solutions
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              We specialize in creating clean, professional, and effective digital solutions for businesses of all sizes. Our approach combines modern design principles with solid technical expertise to deliver websites that not only look great but perform exceptionally.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              Whether you need a new website, a redesign, or strategic guidance for your online presence, we're here to help you succeed in the digital landscape.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-8">
              <div>
                <p className="font-outfit text-3xl font-bold text-slate-900">100%</p>
                <p className="text-sm text-slate-500">Client Focus</p>
              </div>
              <div>
                <p className="font-outfit text-3xl font-bold text-slate-900">Fast</p>
                <p className="text-sm text-slate-500">Delivery</p>
              </div>
              <div>
                <p className="font-outfit text-3xl font-bold text-slate-900">Quality</p>
                <p className="text-sm text-slate-500">Guaranteed</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-sm font-medium tracking-widest uppercase text-blue-600 mb-4">
            Get In Touch
          </p>
          <h2 className="font-outfit text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
            Let's Work Together
          </h2>
          <p className="text-slate-600 leading-relaxed mb-12">
            Ready to start your project? Get in touch and let's discuss how we can help bring your vision to life.
          </p>
          
          {/* Contact Cards */}
          <div className="grid sm:grid-cols-2 gap-6">
            {/* Phone Card */}
            <a 
              href="tel:7184165246"
              data-testid="contact-phone-link"
              className="contact-card p-8 rounded-2xl text-center group hover:shadow-lg transition-shadow duration-300"
            >
              <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-100 transition-colors duration-300">
                <Phone className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-sm text-slate-500 mb-2">Call Us</p>
              <p className="font-outfit text-xl font-semibold text-slate-900">(718) 416-5246</p>
            </a>
            
            {/* Email Card */}
            <a 
              href="mailto:ricardoroquel56@gmail.com"
              data-testid="contact-email-link"
              className="contact-card p-8 rounded-2xl text-center group hover:shadow-lg transition-shadow duration-300"
            >
              <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-100 transition-colors duration-300">
                <Mail className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-sm text-slate-500 mb-2">Email Us</p>
              <p className="font-outfit text-lg font-semibold text-slate-900 break-all">ricardoroquel56@gmail.com</p>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => {
  return (
    <footer data-testid="footer-section" className="bg-slate-900 py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div>
            <p className="font-outfit text-xl font-semibold text-white mb-1">
              Roquel Digital Solutions
            </p>
            <p className="text-slate-400 text-sm">
              Professional Web Development & Design
            </p>
          </div>
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Roquel Digital Solutions. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Navigation
const Navigation = () => {
  return (
    <nav data-testid="navigation" className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-16">
          <a href="/" className="font-outfit text-lg font-semibold text-slate-900">
            Roquel Digital
          </a>
          <div className="hidden md:flex items-center gap-8">
            <a 
              href="#services" 
              className="text-sm text-slate-600 hover:text-slate-900 transition-colors duration-200"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Services
            </a>
            <a 
              href="#about" 
              className="text-sm text-slate-600 hover:text-slate-900 transition-colors duration-200"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              About
            </a>
            <a 
              href="#contact" 
              className="text-sm text-slate-600 hover:text-slate-900 transition-colors duration-200"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Contact
            </a>
          </div>
          <Button 
            data-testid="nav-contact-btn"
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white font-outfit text-sm rounded-full px-5"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Get in Touch
          </Button>
        </div>
      </div>
    </nav>
  );
};

// Main App
function App() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        <HeroSection />
        <ServicesSection />
        <AboutSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
